import {
  ArrowRight,
  Brain,
  ShieldCheck,
  Target,
  Workflow,
  Layers,
  Cpu,
  Activity,
  Eye,
  Bot,
  Warehouse,
  ClipboardCheck,
  Compass,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import SEO from "../components/SEO";

const HERO_IMAGE =
  "/images/image-18.webp";

const ROADMAP_IMAGE =
  "/images/image-12.webp";

const BRAND_BLUE = "#025979";
const BRAND_BLUE_DARK = "#03738C";

// --- Atoms (mirrored from PlatformsPage / PrivaPage design system) ---------
const Eyebrow = ({ children, tone = "blue" }) => (
  <p
    className={`text-[11px] font-semibold tracking-[0.22em] uppercase mb-3 ${
      tone === "purple" ? "text-[#7C3AED]" : "text-[#025979]"
    }`}
  >
    {children}
  </p>
);

const H2 = ({ children, className = "" }) => (
  <h2
    className={`text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight text-[#0F172A] leading-[1.1] ${className}`}
    style={{ fontFamily: "Chivo, sans-serif" }}
  >
    {children}
  </h2>
);

const Lead = ({ children, className = "" }) => (
  <p
    className={`mt-5 text-base sm:text-lg text-[#475569] leading-relaxed max-w-3xl ${className}`}
  >
    {children}
  </p>
);

const PrimaryButton = ({ href, onClick, children, testId, target, color = "blue" }) => {
  const bg = color === "blue" ? "#2563EB" : BRAND_BLUE;
  const bgHover = color === "blue" ? "#1D4ED8" : BRAND_BLUE_DARK;
  const extra = target ? { target, rel: "noopener noreferrer" } : {};
  return (
    <a
      href={href}
      onClick={onClick}
      data-testid={testId}
      {...extra}
      className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide text-white transition-colors"
      style={{ backgroundColor: bg }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = bgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = bg)}
    >
      {children}
      <ArrowRight
        className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
        strokeWidth={2}
      />
    </a>
  );
};

const OutlineButtonOnDark = ({ href, onClick, children, testId }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 transition-colors backdrop-blur-sm"
  >
    {children}
    <ArrowRight
      className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
      strokeWidth={2}
    />
  </a>
);

const SectionDivider = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="h-px bg-gradient-to-r from-transparent via-[#E5E7EB] to-transparent" />
  </div>
);

// --- 1. HERO ---------------------------------------------------------------
const Hero = () => (
  <section
    data-testid="innovation-hero"
    className="relative overflow-hidden text-white"
    style={{
      background:
        "linear-gradient(135deg, #0A1330 0%, #0E1A3F 35%, #0B1734 70%, #050B22 100%)",
    }}
  >
    {/* Full-bleed Innovation hero artwork */}
    <img
      src={HERO_IMAGE}
      alt="PrimeS4 AI innovation ecosystem — applied enterprise AI, operational intelligence, SAP-aligned innovation"
      className="absolute inset-0 w-full h-full object-cover object-right select-none pointer-events-none"
      loading="eager"
      draggable="false"
      data-testid="innovation-hero-artwork"
      aria-hidden="true"
    />
    {/* Left-side wash so copy stays legible */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, rgba(5,11,34,0.94) 0%, rgba(11,23,52,0.78) 30%, rgba(11,23,52,0.35) 55%, rgba(11,23,52,0.05) 80%, rgba(11,23,52,0) 100%)",
      }}
    />
    {/* Enterprise grid */}
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.06]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
      }}
    />
    {/* Soft accent glows */}
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute -top-40 -left-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(circle, #1E40AF 0%, transparent 65%)" }}
      />
      <div
        className="absolute top-1/3 -right-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 65%)" }}
      />
    </div>
    {/* Bottom hairline */}
    <div
      className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(167,139,250,0.45) 50%, transparent 100%)",
      }}
    />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-36">
      <div className="max-w-2xl" data-testid="innovation-hero-copy">
        <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#A78BFA] mb-6 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-[#A78BFA]" />
          Innovation
        </p>
        <h1
          className="text-4xl sm:text-5xl lg:text-[52px] xl:text-[56px] font-bold tracking-tight leading-[1.05]"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="innovation-hero-headline"
        >
          <span>Engineering the </span>
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: "linear-gradient(90deg, #5EB8D8 0%, #A78BFA 100%)" }}
          >
            Autonomous Enterprise
          </span>
        </h1>
        <p
          className="mt-8 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl"
          data-testid="innovation-hero-sub"
        >
          Applied enterprise AI, operational intelligence, SAP-native innovation, and intelligent orchestration designed for real-world execution.
        </p>
        <div className="mt-12 flex flex-wrap gap-3" data-testid="innovation-hero-ctas">
          <PrimaryButton href="/priva" testId="innovation-hero-cta-priva" color="blue">
            Explore PRIVA
          </PrimaryButton>
          <OutlineButtonOnDark href="/platforms" testId="innovation-hero-cta-platforms">
            Explore Platforms
          </OutlineButtonOnDark>
        </div>
        <div
          className="mt-16 grid sm:grid-cols-3 gap-5 max-w-xl"
          data-testid="innovation-hero-tags"
        >
          {[
            { icon: ShieldCheck, label: "SAP-Aligned" },
            { icon: Brain, label: "AI-Powered" },
            { icon: Target, label: "Enterprise-Ready" },
          ].map((it) => (
            <div
              key={it.label}
              className="flex items-center gap-3 text-[12px] font-semibold tracking-[0.14em] uppercase text-white/85"
              data-testid={`innovation-hero-tag-${it.label
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              <it.icon className="w-4 h-4 shrink-0 text-[#A78BFA]" strokeWidth={1.5} />
              <span>{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// --- 2. INNOVATION PHILOSOPHY ----------------------------------------------
const Philosophy = () => {
  const cards = [
    {
      icon: Activity,
      title: "Operational Intelligence",
      desc: "Real-time visibility, enterprise insights, and contextual decision support.",
    },
    {
      icon: Workflow,
      title: "Intelligent Orchestration",
      desc: "Connected workflows, intelligent coordination, and execution management.",
    },
    {
      icon: Brain,
      title: "Applied Enterprise AI",
      desc: "AI designed for operational outcomes rather than generic experimentation.",
    },
    {
      icon: Layers,
      title: "SAP-Native Innovation",
      desc: "Innovation aligned with SAP ecosystems, BTP, and enterprise operations.",
    },
  ];
  return (
    <section
      data-testid="section-innovation-philosophy"
      className="py-20 lg:py-28 bg-gradient-to-b from-white via-[#FAFAFF] to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Eyebrow tone="purple">Innovation Philosophy</Eyebrow>
          <H2>From AI Assistants to Intelligent Enterprise Execution</H2>
          <Lead>
            PrimeS4 AI focuses on operational intelligence, intelligent orchestration, applied enterprise AI, and SAP-aligned innovation frameworks that solve real business problems across supply chain, warehousing, logistics, and enterprise operations.
          </Lead>
        </div>

        <div
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="philosophy-cards"
        >
          {cards.map((c) => (
            <div
              key={c.title}
              className="group bg-white border border-[#E5E7EB] rounded-sm p-6 transition-all duration-200 hover:border-[#A78BFA] hover:shadow-md hover:-translate-y-0.5"
              data-testid={`philosophy-card-${c.title
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              <div
                className="w-10 h-10 rounded-sm flex items-center justify-center mb-5"
                style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)" }}
              >
                <c.icon className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.6} />
              </div>
              <h3 className="text-[15px] font-semibold text-[#0F172A] leading-snug">
                {c.title}
              </h3>
              <p className="mt-2 text-[12.5px] text-[#475569] leading-relaxed">
                {c.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- 3. ROADMAP / FUTURE VISION --------------------------------------------
const Roadmap = () => (
  <section
    data-testid="section-innovation-roadmap"
    className="py-20 lg:py-28 bg-[#F8FAFC] border-y border-[#E5E7EB]"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto">
        <Eyebrow tone="purple">Roadmap & Future Vision</Eyebrow>
        <H2 className="!mx-auto">PrimeS4 AI Roadmap &amp; Future Vision</H2>
        <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed">
          Building the next generation of intelligent enterprise operations.
        </p>
      </div>

      <div
        className="mt-14 lg:mt-16 max-w-6xl mx-auto"
        data-testid="roadmap-image-frame"
      >
        <div
          className="relative rounded-md overflow-hidden bg-white border"
          style={{
            borderColor: "#E5E7EB",
            boxShadow: "0 30px 80px rgba(15,23,42,0.08)",
          }}
        >
          <div
            className="h-1"
            style={{
              background: "linear-gradient(90deg, #5EB8D8 0%, #7C3AED 50%, #5EB8D8 100%)",
            }}
          />
          <img
            src={ROADMAP_IMAGE}
            alt="PrimeS4 AI Roadmap & Future Vision — Operational Visibility, Embedded Intelligence, PRIVA Operational Intelligence Layer, Operational Assistants, and Autonomous Enterprise Operations"
            className="w-full h-auto block"
            loading="lazy"
            data-testid="roadmap-image"
          />
        </div>
      </div>

      <p
        className="mt-10 text-center text-base sm:text-lg text-[#475569] leading-relaxed max-w-3xl mx-auto"
        data-testid="roadmap-supporting-text"
      >
        From operational visibility to autonomous enterprise execution, PrimeS4 AI is building the foundation for intelligent, connected, and AI-assisted enterprise operations.
      </p>
    </div>
  </section>
);

// --- 4. CURRENT INNOVATION AREAS -------------------------------------------
const CurrentInnovationAreas = () => {
  const items = [
    {
      icon: Cpu,
      title: "SmartEWM",
      desc: "AI-assisted warehouse execution, operational intelligence, and intelligent SAP EWM support.",
    },
    {
      icon: ClipboardCheck,
      title: "SmartInspect",
      desc: "AI-powered inspection, evidence capture, and operational compliance.",
    },
    {
      icon: Warehouse,
      title: "WarehouseX",
      desc: "AI-native warehouse transformation and operational intelligence platform.",
    },
    {
      icon: Workflow,
      title: "Intelligent Orchestration",
      desc: "Connected operational workflows and enterprise coordination.",
    },
    {
      icon: Eye,
      title: "Operational Visibility",
      desc: "Real-time enterprise signals, analytics, and monitoring.",
    },
    {
      icon: Bot,
      title: "Future AI Agents",
      desc: "Emerging autonomous operational agents across enterprise workflows.",
    },
  ];
  return (
    <section
      data-testid="section-current-innovation"
      className="py-20 lg:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Eyebrow>Current Focus</Eyebrow>
          <H2>Current Innovation Areas</H2>
          <Lead>
            Active innovation tracks where applied AI is delivering operational outcomes today across the PrimeS4 AI ecosystem.
          </Lead>
        </div>

        <div
          className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="current-innovation-grid"
        >
          {items.map((it) => (
            <div
              key={it.title}
              className="group relative bg-white border border-[#E5E7EB] rounded-sm p-6 transition-all duration-200 hover:border-[#5EB8D8] hover:shadow-md hover:-translate-y-0.5"
              data-testid={`current-innovation-${it.title
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              <div
                className="w-10 h-10 rounded-sm flex items-center justify-center mb-5"
                style={{ background: "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)" }}
              >
                <it.icon className="w-4 h-4 text-[#025979]" strokeWidth={1.6} />
              </div>
              <h3 className="text-[15px] font-semibold text-[#0F172A] leading-snug">
                {it.title}
              </h3>
              <p className="mt-2 text-[12.5px] text-[#475569] leading-relaxed">
                {it.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const INNOVATION_STACK_IMAGE =
  "/images/image-16.webp";

// --- 5. SAP + AI INNOVATION FOUNDATION -------------------------------------
const InnovationStack = () => (
  <section
    data-testid="section-innovation-stack"
    className="py-20 lg:py-28 bg-white"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto">
        <Eyebrow tone="purple">SAP + AI Foundation</Eyebrow>
        <H2 className="!mx-auto">SAP-Native AI Innovation Stack</H2>
        <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed">
          PrimeS4 AI combines SAP-native foundations, operational intelligence, MCP-based contextual integration, intelligent orchestration, and multi-model AI frameworks to power connected enterprise operations.
        </p>
        <p className="mt-3 text-sm sm:text-[15px] text-[#64748B] leading-relaxed italic">
          Built for scalable, secure, and enterprise-grade intelligent execution.
        </p>
      </div>

      <div
        className="mt-14 lg:mt-16 w-[85%] max-w-5xl mx-auto"
        data-testid="innovation-stack-image-frame"
      >
        <div
          className="relative rounded-md overflow-hidden bg-white border"
          style={{
            borderColor: "#E5E7EB",
            boxShadow: "0 30px 80px rgba(15,23,42,0.08)",
          }}
        >
          <div
            className="h-1"
            style={{
              background: "linear-gradient(90deg, #5EB8D8 0%, #7C3AED 50%, #5EB8D8 100%)",
            }}
          />
          <img
            src={INNOVATION_STACK_IMAGE}
            alt="SAP-Native AI Innovation Stack — PRIVA intelligence layer connecting SAP Foundation (SAP BTP, SAP Business AI, Joule), Operational Context Layer (MCP, Enterprise Integration, Operational Data Intelligence, AI Orchestration), and Intelligence Models (OpenAI, Claude, Gemini)"
            className="w-full h-auto block"
            loading="lazy"
            data-testid="innovation-stack-image"
          />
        </div>
      </div>

      <p
        className="mt-10 text-center text-sm sm:text-[15px] font-medium tracking-wide text-[#1E3A8A]/80"
        data-testid="innovation-stack-supporting-text"
      >
        From operational visibility to autonomous enterprise execution.
      </p>
    </div>
  </section>
);

// --- 6. CLOSING CTA --------------------------------------------------------
const ClosingCta = () => (
  <section
    data-testid="section-innovation-closing"
    className="relative py-24 lg:py-28 text-white overflow-hidden"
    style={{
      background: "linear-gradient(135deg, #0A1330 0%, #0E1A3F 50%, #0B1734 100%)",
    }}
  >
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute top-0 left-1/3 w-[420px] h-[420px] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle, #025979 0%, transparent 60%)" }}
      />
      <div
        className="absolute bottom-0 right-1/4 w-[360px] h-[360px] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 60%)" }}
      />
    </div>
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.05]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at center, black 60%, transparent 95%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 60%, transparent 95%)",
      }}
    />

    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#A78BFA] mb-4 flex items-center justify-center gap-3">
        <span className="inline-block h-px w-8 bg-[#A78BFA]" />
        <Compass className="w-3.5 h-3.5" strokeWidth={1.8} />
        Innovation Forward
        <span className="inline-block h-px w-8 bg-[#A78BFA]" />
      </p>
      <h2
        className="text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-tight leading-[1.1]"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Building the Future of{" "}
        <span
          className="bg-clip-text text-transparent"
          style={{ backgroundImage: "linear-gradient(90deg, #5EB8D8 0%, #A78BFA 100%)" }}
        >
          Intelligent Enterprise Operations
        </span>
      </h2>
      <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
        PrimeS4 AI combines operational intelligence, intelligent execution, SAP-native innovation, and enterprise AI to accelerate the autonomous enterprise journey.
      </p>
      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          data-testid="innovation-closing-cta-demo"
          onClick={() => window.dispatchEvent(new CustomEvent("requestDemo:open"))}
          className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide text-white transition-colors"
          style={{ backgroundColor: "#2563EB" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1D4ED8")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2563EB")}
        >
          Request Demo
          <ArrowRight
            className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
            strokeWidth={2}
          />
        </button>
        <OutlineButtonOnDark href="/priva" testId="innovation-closing-cta-priva">
          Explore PRIVA
        </OutlineButtonOnDark>
        <OutlineButtonOnDark href="/platforms" testId="innovation-closing-cta-platforms">
          Explore Platforms
        </OutlineButtonOnDark>
      </div>
    </div>
  </section>
);

// --- Page ------------------------------------------------------------------
const InnovationPage = () => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="innovation-page">
    <SEO
      title="Innovation & Operational Intelligence | PrimeS4 AI"
      description="PrimeS4's innovation roadmap — AI agents, autonomous operations, PRIVA-driven decision intelligence, and the next decade of SAP-aligned enterprise execution."
      keywords={[
        "PrimeS4 Innovation",
        "Enterprise AI roadmap",
        "AI agents",
        "Autonomous operations",
        "Operational Intelligence",
        "SAP BTP AI",
        "Intelligent warehouse operations",
        "Digital transformation",
      ]}
      canonical="/innovation"
      imageAlt="PrimeS4 AI innovation roadmap and intelligent enterprise stack"
    />
    <Header />
    <main className="flex-1">
      <Hero />
      <Philosophy />
      <SectionDivider />
      <Roadmap />
      <CurrentInnovationAreas />
      <InnovationStack />
      <ClosingCta />
    </main>
    <Footer />
  </div>
);

export default InnovationPage;
